import { UsbPrinter } from '@posx/capacitor-usb-printer';
import { Auth } from '@/capacitors/services/AuthService';
import { SocketManager, SocketState } from './socket.manager'
import { getUsbDevices, setupUsbListeners } from './usb.worker';
import { usePrinterStore } from '@/stores/printer';
import { logError, logInfo, serializeError } from '@/utils/logger';
import { captureException } from '@sentry/vue';
import { ServicesBuilder } from '@/utils/setup';
import { isPlatform } from '@ionic/vue';
import { getPlatform } from '@/utils/misc';
import {
    IPrintJob,
    PrintJobService,
    PrintJobStatus,
    PrinterConnections,
    PrinterProtocols,
    PrinterService,
    PrinterStatus,
} from '@posx/core';
import { EscPosPrinter } from '@posx/core';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import iconv from 'iconv-lite';
import { useConfigStore } from '../stores/config';
import { processTSPLCommands } from '@/utils/tspl-text-converter';
let isRunning = false;
const PRINT_JOB_TIMEOUT = 60 * 60 * 1000; // 1 hour timeout for stale print jobs
// Sort print jobs by print_sequence (lower number prints first)
const sortByPrintSequence = (jobs: { list?: IPrintJob[] }) => {
    jobs.list?.sort((a, b) => (a.print_sequence ?? 999) - (b.print_sequence ?? 999));
};
const isPrinterNotFound = (err: unknown) => (err instanceof Error ? err.message : String(err)).toLowerCase().includes('not found');
// Record one print attempt: count it when printed, otherwise requeue until retries run out, then fail
const updatePrintJobAfterAttempt = async (printJob: IPrintJob, printSucceeded: boolean, retries: number) => {
    const printJobStore = usePrinterStore();
    if (printSucceeded) await printJobStore.savePrintJob({ ...printJob, status: PrintJobStatus.Printed, printed: ++printJob.printed });
    else if (printJob.retries < retries) await printJobStore.savePrintJob({ ...printJob, status: PrintJobStatus.Queued, retries: printJob.retries + 1 });
    else await printJobStore.hardDeleteAndUpdatePrintJob({ ...printJob, status: PrintJobStatus.Failed });
};
export const PrinterWorker = async () => {
    if (isRunning) return;
    try {
        isRunning = true;
        if (secureStorage.getItem('is_main_device') !== 'true') {
            return;
        }
        const socketManager = SocketManager.getInstance();
        const encoder = new TextEncoder();
        const printJobStore = usePrinterStore();
        const configStore = useConfigStore();
        const appConfig = await configStore.appConfig;
        const disablePersistentTcp = appConfig?.config?.receipt?.disable_persistent_tcp;
        const textToRaster = appConfig?.config?.label?.text_to_raster ?? false;
        const printJobService = await printJobStore.getPrintJobService();
        const printerService = await printJobStore.getService();
        const escposEthernetPrintJobs = await printJobService.getMany(
            {
                status: PrintJobStatus.Queued,
                connection_type: PrinterConnections.ETHERNET,
                protocol: PrinterProtocols.ESCPOS,
                save_only: false,
            },
            { page: 1, pageSize: 100 },
        );
        sortByPrintSequence(escposEthernetPrintJobs);
        // ESCPOS && ETHERNET
        for (const printJob of escposEthernetPrintJobs.list || []) {
            // Skip and fail timed-out jobs to prevent queue buildup
            if (Date.now() - printJob.created_at_timestamp > PRINT_JOB_TIMEOUT) {
                await printJobStore.hardDeleteAndUpdatePrintJob({ ...printJob, status: PrintJobStatus.Failed });
                continue;
            }
            const escPosPrinter = new EscPosPrinter();
            let connResult = false;
            const socket = socketManager.getSocket(printJob.printer_ip);
            if(socket && !disablePersistentTcp && socket.state === SocketState.OPENED) {
                escPosPrinter.socket = socket;
                connResult = true;
            } else {
                if (socket?.state === SocketState.OPENED) socket.close(); // close before evicting so the socket is not left half-open
                if (socket?.socketKey) socketManager.removeConnectionBySocketKey(socket.socketKey);
                connResult = await escPosPrinter.connect(printJob.printer_ip);
            }
            const printer = await printerService.getOneByParams({ uid: printJob.printer_uid });
            if (!connResult) {
                if (!printer || printer.status === PrinterStatus.Offline) continue;
                printer.status = PrinterStatus.Offline;
                await printerService.updateOne(printer.id_in_server, printer);
                continue;
            }
            if (escPosPrinter.socket.state !== SocketState.OPENED) {
                if (escPosPrinter.socket?.socketKey) socketManager.removeConnectionBySocketKey(escPosPrinter.socket.socketKey);
                logError('ESCPOS Printer socket status is not opened after connect', {
                    socket_status: escPosPrinter.socket.state,
                });
                if (printer) {
                    printer.status = PrinterStatus.Offline;
                    await printerService.updateOne(printer.id_in_server, printer);
                }
                continue;
            }
            if(!disablePersistentTcp) socketManager.registerConnection(printJob.printer_ip, escPosPrinter.socket);
            let printTimes = printJob.print_times - printJob.printed;
            // compatibility
            printTimes ??= 1;
            for (let i = 0; i < printTimes; i++) {
                // retries
                let retries = 1;
                if (printer?.retries != null) retries = printer.retries;
                try {
                    const bytesResult = await escPosPrinter.interpret(printJob.data);
                    const printResult = await escPosPrinter.write(bytesResult);
                    await updatePrintJobAfterAttempt(printJob, printResult, retries);
                } catch (err) {
                    const extra = { print_job_uid: printJob.uid, printer_uid: printJob.printer_uid }; // lets the failing print job be looked up in print_jobs
                    captureException(err, { extra });
                    logError('Write failed', { ...serializeError(err), ...extra });
                    if (!disablePersistentTcp) { await escPosPrinter.disconnect().catch(() => {}); socketManager.removeConnectionBySocketKey(escPosPrinter.socket?.socketKey); } // close before evicting so the socket is not left half-open
                    await updatePrintJobAfterAttempt(printJob, false, retries);
                    break;
                }
            }
            if(printJob.printed >= printJob.print_times) {
                await printJobStore.hardDeleteAndUpdatePrintJob({ ...printJob, status: PrintJobStatus.Printed });
            }
            // Safe disconnect: check state and wrap in try-catch to handle race conditions
            if (disablePersistentTcp && escPosPrinter.socket?.state === SocketState.OPENED) {
                try {
                    await escPosPrinter.disconnect();
                } catch (err) {
                    // Ignore disconnect errors (socket may have been closed by remote)
                    logError('ESCPOS disconnect error (ignored)', serializeError(err));
                }
            }
        }

        // TSPL && ETHERNET
        const tsplEthernetPrintJobs = await printJobService.getMany(
            {
                status: PrintJobStatus.Queued,
                connection_type: PrinterConnections.ETHERNET,
                protocol: PrinterProtocols.TSPL,
                save_only: false,
            },
            {},
        );
        sortByPrintSequence(tsplEthernetPrintJobs);
        for (const printJob of tsplEthernetPrintJobs?.list || []) {
            if (Date.now() - printJob.created_at_timestamp > PRINT_JOB_TIMEOUT) {
                await printJobStore.hardDeleteAndUpdatePrintJob({ ...printJob, status: PrintJobStatus.Failed });
                continue;
            }
            const escPosPrinter = new EscPosPrinter();
            let connResult = false;
            const socket = socketManager.getSocket(printJob.printer_ip);
            if(socket && !disablePersistentTcp && socket.state === SocketState.OPENED) {
                escPosPrinter.socket = socket;
                connResult = true;
            } else {
                if (socket?.state === SocketState.OPENED) socket.close(); // close before evicting so the socket is not left half-open
                if (socket?.socketKey) socketManager.removeConnectionBySocketKey(socket.socketKey);
                connResult = await escPosPrinter.connect(printJob.printer_ip);
            }
            const printer = await printerService.getOne(printJob.printer_uid);
            if (!connResult) {
                if (!printer) continue;
                printer.status = PrinterStatus.Offline;
                await printerService.updateOne(printer.id_in_server, printer);
                continue;
            }
            if (escPosPrinter.socket.state !== SocketState.OPENED) {
                if (escPosPrinter.socket?.socketKey) socketManager.removeConnectionBySocketKey(escPosPrinter.socket.socketKey);
                logError('ESCPOS Printer socket status is not opened after connect', {
                    socket_status: escPosPrinter.socket.state,
                });
                if (printer) {
                    printer.status = PrinterStatus.Offline;
                    await printerService.updateOne(printer.id_in_server, printer);
                }
                continue;
            }
            if(!disablePersistentTcp) socketManager.registerConnection(printJob.printer_ip, escPosPrinter.socket);
            // retries
            let retries = 1;
            if (printer?.retries != null) retries = printer.retries;
            try {
                // Convert RTEXT commands with non-ASCII to BITMAP if textToRaster enabled and has RTEXT
                const shouldConvert = textToRaster && /^rtext\s/im.test(printJob.commands);
                let buffer: Uint8Array;
                if (shouldConvert) buffer = await processTSPLCommands(printJob.commands);
                else buffer = new Uint8Array(iconv.encode(printJob.commands, 'gb18030'));

                const printResult = await escPosPrinter.write(buffer);
                await updatePrintJobAfterAttempt(printJob, printResult, retries);
            } catch (err) {
                captureException(err);
                logError('Write failed', serializeError(err));
                if (!disablePersistentTcp) { await escPosPrinter.disconnect().catch(() => {}); socketManager.removeConnectionBySocketKey(escPosPrinter.socket?.socketKey); } // close before evicting so the socket is not left half-open
                await updatePrintJobAfterAttempt(printJob, false, retries);
            }
            if(printJob.printed >= printJob.print_times) {
                await printJobStore.hardDeleteAndUpdatePrintJob({ ...printJob, status: PrintJobStatus.Printed });
            }
            // Safe disconnect: check state and wrap in try-catch to handle race conditions
            if (disablePersistentTcp && escPosPrinter.socket?.state === SocketState.OPENED) {
                try {
                    await escPosPrinter.disconnect();
                } catch (err) {
                    // Ignore disconnect errors (socket may have been closed by remote)
                    logError('TSPL disconnect error (ignored)', serializeError(err));
                }
            }
        }

        if (isPlatform('electron')) {
            // ESCPOS && Windows Driver
            const escposUsbPrintJobs = await printJobService.getMany(
                {
                    status: PrintJobStatus.Queued,
                    connection_type: PrinterConnections.WINDOWS_DRIVER,
                    protocol: PrinterProtocols.ESCPOS,
                    save_only: false,
                },
                {},
            );
            sortByPrintSequence(escposUsbPrintJobs);
            for (const printJob of escposUsbPrintJobs?.list || []) {
                if (Date.now() - printJob.created_at_timestamp > PRINT_JOB_TIMEOUT) {
                    await printJobStore.hardDeleteAndUpdatePrintJob({ ...printJob, status: PrintJobStatus.Failed });
                    continue;
                }
                if (!printJob.driver_printer_name) continue;
                const printer = await printerService.getOneByParams({ uid: printJob.printer_uid });
                const retries = printer?.retries ?? 1;
                let usbPrintTimes = printJob.print_times - printJob.printed;
                usbPrintTimes ??= 1;
                try {
                    for (let i = 0; i < usbPrintTimes; i++) {
                        const escPosPrinter = new EscPosPrinter();
                        const bytesResult = await escPosPrinter.interpret(printJob.data);
                        await window.electronPrinter.directPrint(printJob.driver_printer_name, bytesResult, printJob.uid);
                        await updatePrintJobAfterAttempt(printJob, true, retries);
                    }
                    if (printJob.printed >= printJob.print_times) await printJobStore.hardDeleteAndUpdatePrintJob({ ...printJob, status: PrintJobStatus.Printed });
                } catch (err) {
                    const extra = { print_job_uid: printJob.uid, printer_uid: printJob.printer_uid }; // lets the failing print job be looked up in print_jobs
                    if (!isPrinterNotFound(err)) captureException(err, { extra });
                    logError('Windows driver ESCPOS print failed', { ...serializeError(err), printer_name: printJob.driver_printer_name, ...extra });
                    if (printer && printer.status !== PrinterStatus.Offline) { printer.status = PrinterStatus.Offline; await printerService.updateOne(printer.id_in_server, printer); }
                    await updatePrintJobAfterAttempt(printJob, false, retries);
                }
            }

            // TSPL && USB
            const tsplUsbPrintJobs = await printJobService.getMany(
                {
                    status: PrintJobStatus.Queued,
                    connection_type: PrinterConnections.WINDOWS_DRIVER,
                    protocol: PrinterProtocols.TSPL,
                    save_only: false,
                },
                {},
            );
            sortByPrintSequence(tsplUsbPrintJobs);
            for (const printJob of tsplUsbPrintJobs?.list || []) {
                if (Date.now() - printJob.created_at_timestamp > PRINT_JOB_TIMEOUT) {
                    await printJobStore.hardDeleteAndUpdatePrintJob({ ...printJob, status: PrintJobStatus.Failed });
                    continue;
                }
                if (!printJob.driver_printer_name) continue;
                const printer = await printerService.getOneByParams({ uid: printJob.printer_uid });
                const retries = printer?.retries ?? 1;
                let usbPrintTimes = printJob.print_times - printJob.printed;
                usbPrintTimes ??= 1;
                const shouldConvert = textToRaster && /^rtext\s/im.test(printJob.commands);
                let commandsBuffer: Uint8Array;
                if (shouldConvert) commandsBuffer = await processTSPLCommands(printJob.commands);
                else commandsBuffer = new Uint8Array(iconv.encode(printJob.commands, 'gb18030'));
                try {
                    for (let i = 0; i < usbPrintTimes; i++) {
                        await window.electronPrinter.directPrint(printJob.driver_printer_name, commandsBuffer, printJob.uid);
                        await updatePrintJobAfterAttempt(printJob, true, retries);
                    }
                    if (printJob.printed >= printJob.print_times) await printJobStore.hardDeleteAndUpdatePrintJob({ ...printJob, status: PrintJobStatus.Printed });
                } catch (err) {
                    if (!isPrinterNotFound(err)) captureException(err);
                    logError('Windows driver TSPL print failed', { ...serializeError(err), printer_name: printJob.driver_printer_name });
                    if (printer) { printer.status = PrinterStatus.Offline; await printerService.updateOne(printer.id_in_server, printer); }
                    await updatePrintJobAfterAttempt(printJob, false, retries);
                }
            }
        }

        // USB (Android only) - handles both ESCPOS and TSPL
        if (getPlatform() === 'android') {
            await setupUsbListeners();
            const usbPrintJobs = await printJobService.getMany({
                status: PrintJobStatus.Queued,
                connection_type: PrinterConnections.USB,
                save_only: false,
            }, {});
            sortByPrintSequence(usbPrintJobs);
            const usbDevices = getUsbDevices();
            for (const printJob of usbPrintJobs?.list || []) {
                if (Date.now() - printJob.created_at_timestamp > PRINT_JOB_TIMEOUT) {
                    await printJobStore.hardDeleteAndUpdatePrintJob({ ...printJob, status: PrintJobStatus.Failed });
                    continue;
                }
                const printer = await printerService.getOneByParams({ uid: printJob.printer_uid });
                if (!printer) continue;
                // Use portPath (stable across replug) or serialNumber based on use_device_name setting
                const printerExt = printer as typeof printer & { use_device_name?: boolean; usb_device_name?: string };
                const identifier = printerExt.use_device_name ? printerExt.usb_device_name : printer.usb_serial_number;
                if (!identifier) {
                    if (printer.status !== PrinterStatus.Offline) { logError('USB printer has no identifier configured', { printer_uid: printJob.printer_uid, printer_name: printer.name }); printer.status = PrinterStatus.Offline; await printerService.updateOne(printer.id_in_server, printer); }
                    continue;
                }
                const device = printerExt.use_device_name ? usbDevices.find(d => d.portPath === identifier) : usbDevices.find(d => d.serialNumber === identifier);
                if (!device) {
                    if (printer.status !== PrinterStatus.Offline) { logError('USB device not found or permission denied', { identifier, printer_name: printer.name }); printer.status = PrinterStatus.Offline; await printerService.updateOne(printer.id_in_server, printer); }
                    continue;
                }
                const { granted } = await UsbPrinter.requestPermission({ deviceId: device.deviceId });
                if (!granted) {
                    if (printer.status !== PrinterStatus.Offline) { logError('USB permission denied', { identifier, printer_name: printer.name }); printer.status = PrinterStatus.Offline; await printerService.updateOne(printer.id_in_server, printer); }
                    continue;
                }
                let connectResult: { connected: boolean };
                try { connectResult = await UsbPrinter.connect({ deviceId: device.deviceId }); }
                catch (e) {
                    if (printer.status !== PrinterStatus.Offline) { logError('USB connect threw', { error: String(e), deviceId: device.deviceId, printer_name: printer.name }); printer.status = PrinterStatus.Offline; await printerService.updateOne(printer.id_in_server, printer); }
                    continue;
                }
                const { connected } = connectResult;
                if (!connected) {
                    if (printer.status !== PrinterStatus.Offline) { logError('USB connect failed', { identifier, printer_name: printer.name }); printer.status = PrinterStatus.Offline; await printerService.updateOne(printer.id_in_server, printer); }
                    continue;
                }
                if (printer.status !== PrinterStatus.Online) { logInfo('USB printer back online', { identifier, printer_name: printer.name }); printer.status = PrinterStatus.Online; await printerService.updateOne(printer.id_in_server, printer); }
                const retries = printer.retries ?? 1;
                try {
                    // Prepare data based on protocol
                    let hexData: string;
                    if (printJob.protocol === PrinterProtocols.TSPL) {
                        const shouldConvert = textToRaster && /^rtext\s/im.test(printJob.commands);
                        const buffer = shouldConvert ? await processTSPLCommands(printJob.commands) : new Uint8Array(iconv.encode(printJob.commands, 'gb18030'));
                        hexData = Array.from(buffer).map(b => b.toString(16).padStart(2, '0')).join('');
                    } else {
                        const escPosPrinter = new EscPosPrinter();
                        const bytesResult = await escPosPrinter.interpret(printJob.data);
                        hexData = Array.from(bytesResult).map(b => b.toString(16).padStart(2, '0')).join('');
                    }
                    let printTimes = printJob.print_times - printJob.printed;
                    printTimes ??= 1;
                    for (let i = 0; i < printTimes; i++) {
                        await UsbPrinter.print({ deviceId: device.deviceId, data: hexData });
                        await updatePrintJobAfterAttempt(printJob, true, retries);
                    }
                    if (printJob.printed >= printJob.print_times)
                        await printJobStore.hardDeleteAndUpdatePrintJob({ ...printJob, status: PrintJobStatus.Printed });
                } catch (err) {
                    const extra = { print_job_uid: printJob.uid, printer_uid: printJob.printer_uid }; // lets the failing print job be looked up in print_jobs
                    captureException(err, { extra });
                    logError('USB print failed', { ...serializeError(err), ...extra });
                    await updatePrintJobAfterAttempt(printJob, false, retries);
                } finally {
                    try { await UsbPrinter.disconnect({ deviceId: device.deviceId }); }
                    catch (err) { logError('USB disconnect failed (ignored)', serializeError(err)); }
                }
            }
        }

        // due to core bug, the save-only print jobs have queued status, so we need to update them to printed status
        const saveOnlyPrintJobs = await printJobService.getMany(
            {
                status: PrintJobStatus.Queued,
                save_only: true,
            },
            {},
        );
        for (const printJob of saveOnlyPrintJobs?.list || []) {
            await printJobStore.hardDeleteAndUpdatePrintJob({ ...printJob, status: PrintJobStatus.Printed }); // delete after processing to prevent IndexedDB accumulation
        }
    } catch (err) {
        captureException(err);
        logError('PrinterWorker unexpected error', serializeError(err));
    } finally {
        isRunning = false;
    }
};
